import { getUser } from "@synthesis/shared-auth";
import type { SelectItem, ServerSearch } from "@synthesis/shared-ui";
import { useQuery } from "@tanstack/react-query";

// ------------------------------ TYPES ------------------------------

export type RoleFneSearchParams = {
  param?: string;
  offset?: number;
  count?: number;
  roles: string[];
  roadsIds?: string[];
};

export type RoleFneSearchResult = {
  id: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  middleName?: string | null;
  position?: string | null;
  groups?: Array<{ name?: string | null }> | null;
  realmRoles?: string[] | null;
  attributes?: {
    middleName?: string[];
  } | null;
};

export type RoleFneSearchResultMapper = (user: RoleFneSearchResult) => SelectItem;

// ------------------------------ CONSTANTS ------------------------------

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_SERVER_URI;
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM;

const SEARCH_PATH = `/auth/realms/${KEYCLOAK_REALM}/custom-realm-resource-factory-user-search/search-role-fne`;

const MIN_SEARCH_LENGTH = 2;
export const ROLE_FNE_DEFAULT_PAGE_SIZE = 10;

const getSearchUrl = (): string => {
  if (import.meta.env.DEV) {
    return SEARCH_PATH;
  }

  return `${KEYCLOAK_URL}${SEARCH_PATH}`;
};

// ------------------------------ FETCH ------------------------------

const searchRoleFne = async (params: RoleFneSearchParams, signal?: AbortSignal): Promise<RoleFneSearchResult[]> => {
  const token = getUser()?.access_token;

  if (!token) {
    throw new Error("Not authenticated");
  }

  const url = new URL(getSearchUrl(), import.meta.env.DEV ? window.location.origin : undefined);

  if (params.param) {
    url.searchParams.set("param", params.param);
  }

  if (params.offset !== undefined) {
    url.searchParams.set("offset", String(params.offset));
  }

  if (params.count !== undefined) {
    url.searchParams.set("count", String(params.count));
  }

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ rolesNames: params.roles, roadsIds: params.roadsIds ?? [] }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Keycloak search failed: ${response.status}`);
  }

  return response.json();
};

// ------------------------------ TANSTACK QUERY ------------------------------

export const roleFneSearchQueryKey = (params: RoleFneSearchParams) => ["role-fne-search", params] as const;

export const useRoleFneSearch = <TData = RoleFneSearchResult[]>(params: RoleFneSearchParams, options?: { enabled?: boolean }) =>
  useQuery<RoleFneSearchResult[], Error, TData>({
    queryKey: roleFneSearchQueryKey(params),
    queryFn: async ({ signal }) => searchRoleFne(params, signal),
    enabled: options?.enabled ?? (params.param?.length ?? 0) >= MIN_SEARCH_LENGTH,
  });

// ------------------------------ SERVER SEARCH ------------------------------

const defaultUserToSelectItem = (user: RoleFneSearchResult): SelectItem => ({
  value: user.id,
  label: [user.lastName, user.firstName, user.middleName].filter(Boolean).join(" ") || user.email || "—",
  data: user,
});

export const createRoleFneServerSearch = (
  roles: string[],
  pageSize = ROLE_FNE_DEFAULT_PAGE_SIZE,
  mapper: RoleFneSearchResultMapper = defaultUserToSelectItem,
  roadsIds: string[] = [],
): ServerSearch => ({
  fetchItems: async (search, page, signal) => {
    const trimmedQuery = search?.trim();

    if (trimmedQuery && trimmedQuery.length < MIN_SEARCH_LENGTH) {
      return [];
    }

    const param = trimmedQuery || undefined;
    const results = await searchRoleFne({ param, offset: (page - 1) * pageSize, count: pageSize, roles, roadsIds }, signal);

    return results.map(mapper);
  },
  getNextPageParam: (lastPage, allPages) => {
    if (!Array.isArray(lastPage) || lastPage.length < pageSize) {
      return undefined;
    }

    return allPages.length + 1;
  },
});
